import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute, PublicRoute, AdminRoute } from './AuthRoutes';
import useAuthStore from '../store/Auth';
import LoadingComponent from '@/components/LoadingComponent';

import DashboardLayout from '../layouts/DashboardLayout/DashboardLayout';
import { ROUTES } from '.';

const Insights = lazy(() => import('@/pages/insights/Insights'));
const Projects = lazy(() => import('@/pages/projects/Projects'));
const CreateProject = lazy(
  () => import('@/pages/projects/CreateProject/CreateProject')
);
const ProjectPage = lazy(
  () => import('@/pages/projects/ProjectPage/ProjectPage')
);
const Teams = lazy(() => import('@/pages/teams/Teams'));
const TeamPage = lazy(() => import('@/pages/teams/TeamPage/TeamPage'));
const Permissions = lazy(() => import('@/pages/permissions/Permissions'));
const Settings = lazy(() => import('../pages/settings/Settings'));
const Profile = lazy(() => import('../pages/settings/Profile/Profile'));
const IntegrationsPage = lazy(
  () => import('../pages/settings/Integrations/Integrations')
);
const SlackIntegrationPage = lazy(
  () => import('../pages/integrations/Slack/Slack')
);
const OpenAIIntegrationPage = lazy(
  () => import('../pages/integrations/OpenAI/OpenAI')
);
const JiraIntegrationPage = lazy(
  () => import('../pages/integrations/Jira/Jira')
);
const Login = lazy(() => import('@/pages/auth/login/Login'));
const SignUp = lazy(() => import('@/pages/auth/signup/SignUp'));
const NotFound = lazy(() => import('../pages/notFound/NotFound'));
const Plans = lazy(() => import('@/pages/plans/Plans'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="h-full w-full flex items-center justify-center">
          <LoadingComponent />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export default function AppRoutes() {
  const { isAuthenticated, user } = useAuthStore();
  const isAdmin = user?.admin ?? false;
  return (
    <Routes>
      <Route
        path={ROUTES.INSIGHTS}
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <SuspenseWrapper>
              <Insights />
            </SuspenseWrapper>
          }
        />
        <Route
          path={ROUTES.INSIGHTS_ALIAS}
          element={
            <SuspenseWrapper>
              <Insights />
            </SuspenseWrapper>
          }
        />

        {/* ========= projects ======= */}
        <Route
          path={ROUTES.PROJECTS}
          element={
            <SuspenseWrapper>
              <Projects />
            </SuspenseWrapper>
          }
        />
        <Route
          path={ROUTES.CREATE_PROJECTS}
          element={
            <SuspenseWrapper>
              <CreateProject />
            </SuspenseWrapper>
          }
        />
        <Route
          path={ROUTES.SINGLE_PROJECT}
          element={
            <SuspenseWrapper>
              <ProjectPage />
            </SuspenseWrapper>
          }
        />

        {/* ========= teams ======= */}
        <Route
          path={ROUTES.TEAMS}
          element={
            <SuspenseWrapper>
              <Teams />
            </SuspenseWrapper>
          }
        />
        <Route
          path={ROUTES.SINGLE_TEAM}
          element={
            <SuspenseWrapper>
              <TeamPage />
            </SuspenseWrapper>
          }
        />

        {/* ========= plans ======= */}
        <Route
          path={ROUTES.PLANS}
          element={
            <SuspenseWrapper>
              <Plans />
            </SuspenseWrapper>
          }
        />

        <Route
          path={ROUTES.PERMISSIONS}
          element={
            <SuspenseWrapper>
              <Permissions />
            </SuspenseWrapper>
          }
        />
        <Route
          path={ROUTES.SETTINGS}
          element={
            <SuspenseWrapper>
              <Settings />
            </SuspenseWrapper>
          }
        >
          <Route
            index
            element={<Navigate to={ROUTES.PERSONAL_INFO} replace />}
          />
          <Route
            path={ROUTES.PERSONAL_INFO}
            element={
              <SuspenseWrapper>
                <Profile />
              </SuspenseWrapper>
            }
          />
          <Route
            path={ROUTES.INTEGRATIONS}
            element={
              <AdminRoute isAdmin={isAdmin}>
                <SuspenseWrapper>
                  <IntegrationsPage />
                </SuspenseWrapper>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.SLACK_INTEGRATION}
            element={
              <AdminRoute isAdmin={isAdmin}>
                <SuspenseWrapper>
                  <SlackIntegrationPage />
                </SuspenseWrapper>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.OPENAI_INTEGRATION}
            element={
              <AdminRoute isAdmin={isAdmin}>
                <SuspenseWrapper>
                  <OpenAIIntegrationPage />
                </SuspenseWrapper>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.JIRA_INTEGRATION}
            element={
              <AdminRoute isAdmin={isAdmin}>
                <SuspenseWrapper>
                  <JiraIntegrationPage />
                </SuspenseWrapper>
              </AdminRoute>
            }
          />
        </Route>
        <Route
          path="/integrations/Slack"
          element={<Navigate to={ROUTES.SLACK_INTEGRATION} replace />}
        />
        <Route
          path="/integrations/OpenAI"
          element={<Navigate to={ROUTES.OPENAI_INTEGRATION} replace />}
        />
        <Route
          path="/integrations/Jira"
          element={<Navigate to={ROUTES.JIRA_INTEGRATION} replace />}
        />
      </Route>

      <Route
        path={ROUTES.LOGIN}
        element={
          <PublicRoute isAuthenticated={isAuthenticated}>
            <SuspenseWrapper>
              <Login />
            </SuspenseWrapper>
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.SIGNUP}
        element={
          <PublicRoute isAuthenticated={isAuthenticated}>
            <SuspenseWrapper>
              <SignUp />
            </SuspenseWrapper>
          </PublicRoute>
        }
      />

      <Route
        path="*"
        element={
          <SuspenseWrapper>
            <NotFound />
          </SuspenseWrapper>
        }
      />
    </Routes>
  );
}
